import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExperienceStatus, getMoodsIds, getTranslatedName, moods } from '@utriper/core';
import { useExperiences } from 'core/contexts/experienceV2';
import { Field, Form } from 'react-final-form';
import { Box, Button, Grid2, InputLabel } from '@mui/material';
import { ResponsivePanel, SelectCheckbox, Slider, GooglePlacesAutocomplete } from '@utriper/core-ui';

const FiltersForm = () => {
    const { i18n, t: translate } = useTranslation();
    const { filters, handleFilters, openFilters, handleToggleFilters } = useExperiences();
    const statusOptions = useMemo(
        () =>
            ExperienceStatus.getIds().map((status) => ({
                id: status,
                name: ExperienceStatus.getTranslatedName(ExperienceStatus.status, status, i18n.language),
            })),
        [i18n.language]
    );
    const modusesOptions = useMemo(
        () =>
            getMoodsIds().map((mood) => ({
                id: mood,
                name: getTranslatedName(moods, mood, i18n.language),
            })),
        [i18n.language]
    );

    const sliderMarks = [
        {
            value: 10,
            label: '10km',
        },
        {
            value: 100,
            label: '100km',
        },
    ];

    function valueLabelFormat(value) {
        return `${value} km`;
    }

    return (
        <Form
            key={'experience-list-filters-form'}
            onSubmit={(values) => {
                handleFilters(values);
                handleToggleFilters();
            }}
            initialValues={{ ...filters }}
            render={({ values, handleSubmit, form }) => {
                const handleChangeStatus = (newStatuses) => {
                    form.change('status', [...newStatuses]);
                };

                const handleChangeMoods = (newMoods) => {
                    form.change('moduses', [...newMoods]);
                };

                const handleChangeRadius = (_event, newRadius) => {
                    form.change('radius', newRadius);
                };

                const handleSubmitForm = (e) => {
                    return handleSubmit(e);
                };

                const actionNext = {
                    label: translate('common.apply'),
                    onClick: handleSubmitForm,
                    type: 'submit',
                };

                const handleBack = (e) => {
                    handleToggleFilters();
                    return form.reset(e);
                };

                const actionBack = {
                    label: translate('common.cancel'),
                    onClick: handleBack,
                    type: 'reset',
                };

                return (
                    <form key={'experience-list-filters-form'} onSubmit={handleSubmit} noValidate style={{ display: 'flex', width: '100%', marginTop: '4px' }}>
                        <Box sx={{ width: '100%', '& .MuiCollapse-root': { width: '100%' } }}>
                            <ResponsivePanel open={openFilters} title={translate('experience_list.filters.title')} handleToggle={handleToggleFilters} hasActions actionBack={actionBack} actionNext={actionNext}>
                                <Grid2 container size={12} direction="column" justifyContent="flex-start" alignItems="flex-start" gap="16px">
                                    <Grid2 container size={12} spacing={2} flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="flex-start">
                                        <Grid2
                                            container
                                            size={{ xs: 12, md: 6 }}
                                            direction="column"
                                            gap="4px"
                                            sx={{
                                                '& .MuiFormControl-root': {
                                                    width: '100%',
                                                },
                                            }}
                                        >
                                            <InputLabel
                                                sx={{
                                                    fontSize: '18px',
                                                    fontWeight: 400,
                                                    color: '#131313',
                                                }}
                                            >
                                                {translate('experience_list.filters.status')}
                                            </InputLabel>
                                            <Field id="status" name="status" component={SelectCheckbox} options={statusOptions} label={translate('experience_list.filters.status_label')} values={values?.status ?? []} onChange={handleChangeStatus} />
                                        </Grid2>
                                        <Grid2
                                            container
                                            size={{ xs: 12, md: 6 }}
                                            direction="column"
                                            gap="4px"
                                            sx={{
                                                '& .MuiFormControl-root': {
                                                    width: '100%',
                                                },
                                            }}
                                        >
                                            <InputLabel
                                                sx={{
                                                    fontSize: '18px',
                                                    fontWeight: 400,
                                                    color: '#131313',
                                                }}
                                            >
                                                {translate('experience_list.filters.moods')}
                                            </InputLabel>
                                            <Field id="moduses" name="moduses" component={SelectCheckbox} options={modusesOptions} label={translate('experience_list.filters.moods_label')} values={values?.moduses ?? []} onChange={handleChangeMoods} />
                                        </Grid2>
                                    </Grid2>

                                    <Grid2 container size={12} spacing={2} flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="flex-start">
                                        <Grid2
                                            container
                                            size={{ xs: 12, md: 6 }}
                                            direction="column"
                                            gap="4px"
                                            order={{ xs: 0, md: 1 }}
                                            sx={{
                                                '& .MuiTypography-root': {
                                                    fontSize: '18px',
                                                    fontWeight: 400,
                                                    color: '#131313',
                                                },
                                                '& .MuiSlider-root': {
                                                    marginX: '16px',
                                                    width: 'auto',
                                                },
                                            }}
                                        >
                                            <Field id="radius" name="radius" component={Slider} label={translate('experience_list.filters.radius')} min={10} max={100} defaultValue={values.radius ?? 10} step={10} marks={sliderMarks} valueLabelDisplay="auto" getAriaValueText={valueLabelFormat} valueLabelFormat={valueLabelFormat} onChange={handleChangeRadius} />
                                        </Grid2>
                                        <Grid2 container size={{ xs: 12, md: 6 }} direction="column" gap="4px" order={{ xs: 1, md: 0 }}>
                                            <InputLabel
                                                sx={{
                                                    fontSize: '18px',
                                                    fontWeight: 400,
                                                    color: '#131313',
                                                }}
                                            >
                                                {translate('experience_list.filters.location')}
                                            </InputLabel>
                                            <Field name="locationData" component={GooglePlacesAutocomplete} label={translate('experience_list.filters.location')} />
                                        </Grid2>
                                    </Grid2>

                                    <Grid2 container size={12} spacing={1} justifyContent="flex-end" alignItems="center">
                                        <Button
                                            sx={{
                                                border: 'solid 1px #13131340',
                                                color: '#131313',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                            }}
                                            onClick={handleBack}
                                            variant="outlined"
                                        >
                                            {translate('common.cancel')}
                                        </Button>
                                        <Button
                                            sx={{
                                                backgroundColor: 'var(--color-utriper-blue)',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                            }}
                                            type="submit"
                                            onClick={handleSubmitForm}
                                            variant="contained"
                                        >
                                            {translate('common.apply')}
                                        </Button>
                                    </Grid2>
                                </Grid2>
                            </ResponsivePanel>
                        </Box>
                    </form>
                );
            }}
        />
    );
};

export default FiltersForm;
